"use client";

import type { PlayerRef } from "@remotion/player";
import type { VideoModel } from "@ziggapp/klap-core/video";
import dynamic from "next/dynamic";
import { FC, ReactNode, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { EditorHelper } from "../../helpers/editor";
import PlayOverlay from "../PlayOverlay";
import ShortPlayerChrome from "../player/ShortPlayerChrome";
import Spinner from "../Spinner";
import { typeScaleClassNames } from "../Typography";

// The inspector's media preview: thumbnail until first play, caption,
// status and a persistent action strip. Its controls intercept clicks
// above the player's playback hit area.

const VideoPlayer = dynamic(() => import("../VideoPlayer"), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 flex items-center justify-center">
      <Spinner className="w-5 h-5 text-white" />
    </div>
  ),
});

/** Shorts are vertical; used until the real short answers with its own. */
const FALLBACK_RATIO = { width: 9, height: 16 };

const CalendarPostPlayer: FC<{
  videoId: string;
  /** The loaded short; null while loading or gone (poster only). */
  video: VideoModel | null;
  /** The post's caption, displayed over the bottom of the preview. */
  title?: string;
  /** Publication status, displayed directly above the caption. */
  statusBadge?: ReactNode;
  /** Schedule, menu and navigation, over the top of the preview. */
  actions?: ReactNode;
  /**
   * The player's width, as a CSS length. The height follows from the
   * short's own aspect ratio, which is why the viewport-height cap lives
   * in this value rather than in a max-height (see the popover).
   */
  width: string;
  className?: string;
}> = ({
  videoId,
  video,
  title,
  statusBadge,
  actions,
  width,
  className,
}) => {
  const [playback, setPlayback] = useState({ videoId, mounted: false });
  // Reset only playback when moving to another video. The surrounding
  // controls keep their DOM identity and keyboard focus across posts.
  if (playback.videoId !== videoId) {
    setPlayback({ videoId, mounted: false });
  }
  const isPlayerMounted = playback.videoId === videoId && playback.mounted;
  const playerRef = useRef<PlayerRef | null>(null);
  const ratio = video?.width && video?.height ? video : FALLBACK_RATIO;

  return (
    <div
      className={twMerge(
        // Media controls always use the dark palette for contrast.
        "dark group relative overflow-hidden bg-slate-950 select-none",
        className,
      )}
      style={{
        aspectRatio: `${ratio.width}/${ratio.height}`,
        // Scale the caption's scrim with the preview frame.
        containerType: "inline-size",
        width,
      }}
    >
      <img
        src={`${process.env.NEXT_PUBLIC_API_URL}/projects/${videoId}/thumbnail`}
        alt=""
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {isPlayerMounted && video ? (
        <div className="absolute inset-0">
          <VideoPlayer
            video={video}
            playerRef={playerRef}
            playerProps={{ autoPlay: true, controls: false }}
            borderRadius={0}
            className="h-full w-full"
          />
        </div>
      ) : null}
      {/* Status and caption stay separate from the playback hit area. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/75 to-transparent px-2 pb-2 pt-[3.5em] text-white"
        style={{ fontSize: "4.6cqw" }}
      >
        {statusBadge ? (
          <div data-calendar-post-status className="mb-1.5 max-w-full">{statusBadge}</div>
        ) : null}
        <div
          data-calendar-post-caption
          className={twMerge(typeScaleClassNames.overline, "min-w-0 w-full line-clamp-3")}
        >
          {title ?? (
            <div className="h-[1em] w-[60%] rounded-full bg-white/40 backdrop-blur-sm" />
          )}
        </div>
      </div>
      {isPlayerMounted && video ? (
        <ShortPlayerChrome
          playerRef={playerRef}
          durationUs={EditorHelper.getLongestTrackDuration(video.tracks) ?? 0}
          fps={30}
          showTimePill={false}
          // md (44px), not the full player's 64px: this frame is a third
          // the schedule column's width, and the lg disc reads as a button
          // stuck on the video rather than the player's own control.
          playOverlaySize="md"
        />
      ) : (
        <button
          type="button"
          aria-label="Play preview"
          disabled={!video}
          onClick={() => setPlayback({ videoId, mounted: true })}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/0 transition-colors hover:bg-black/10 disabled:cursor-default"
        >
          <PlayOverlay size="md" />
        </button>
      )}
      {/* Only the action buttons intercept playback clicks in this strip. */}
      {actions ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 z-40 h-20 bg-gradient-to-b from-black/60 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-center gap-0 p-2 [&_button]:pointer-events-auto">
            {actions}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CalendarPostPlayer;
